import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, TrendingUp, Filter, BarChart3, Check } from "lucide-react";

interface AnalyticsUpgradeProps {
  variant?: "compact" | "full";
}

export function AnalyticsUpgrade({ variant = "compact" }: AnalyticsUpgradeProps) {
  if (variant === "full") {
    return (
      <Card className="border-2 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center py-8 max-w-2xl mx-auto">
            <div className="rounded-full bg-primary/10 p-4 mb-6">
              <Lock className="h-8 w-8 text-primary" />
            </div>

            <h2 className="text-2xl font-bold mb-3">Unlock Advanced Analytics</h2>
            <p className="text-muted-foreground mb-6 text-lg">
              Get deeper insights into your review collection performance and optimize your strategy
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-8 text-left">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                <TrendingUp className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Performance Trends</h3>
                  <p className="text-sm text-muted-foreground">
                    Track your review collection over time with visual charts
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                <Filter className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Conversion Funnel</h3>
                  <p className="text-sm text-muted-foreground">
                    See where customers drop off and get actionable tips
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                <BarChart3 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Source Comparison</h3>
                  <p className="text-sm text-muted-foreground">
                    Compare performance between email and QR code reviews
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-1">Staff Leaderboard</h3>
                  <p className="text-sm text-muted-foreground">
                    Rank team members by reviews and response rates
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/pricing">
                <Button size="lg" className="min-w-[200px]">
                  Upgrade to Lifetime
                </Button>
              </Link>
              <Link href="/pricing">
                <Button size="lg" variant="outline" className="min-w-[200px]">
                  See All Plans
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact variant
  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="pt-6">
        <div className="flex flex-col items-center text-center py-6">
          <div className="rounded-full bg-primary/10 p-3 mb-4">
            <Lock className="h-6 w-6 text-primary" />
          </div>

          <h3 className="text-lg font-semibold mb-2">Unlock Advanced Analytics</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md">
            Upgrade to Lifetime or Pro to access performance trends, conversion funnels,
            source comparisons, and staff leaderboards
          </p>

          <Link href="/pricing">
            <Button>
              View Plans
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
